import { useCallback, useEffect } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { MarketCode } from '@tink/config/src/markets';
import { useTPRedirectMachine, UseTPRedirectMachineProps } from '../hooks/useTPRedirectMachine';
import { ThirdPartyRedirectInstructionsScreen } from '../screens/third-party-redirect/ThirdPartyRedirectInstructionsScreen';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { ThirdPartyRedirectPendingScreen } from '../screens/third-party-redirect/ThirdPartyRedirectPendingScreen';
import { BackButton } from '../../components/organisms/Header/HeaderMolecule';
import { useAppSelector } from '../../stores/redux-store';
import { ProviderEntity } from '../../models/provider';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { ApplicationEvent } from '../../models/analytics';
import { getAutoRedirectMobileQueryParamAnalytic } from '../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/AuthAwaitingThirdpartyAuthenticationContainer/getAutoRedirectMobileQueryParamAnalytic';
import { OpenThirdPartyRedirectScreen } from '../screens/third-party-redirect/OpenThirdPartyRedirectScreen';
import { FlowFinished } from '../../components/screens/FlowFinished/FlowFinished';
import { useUrlQuery } from '../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { ThirdPartyRedirectScreen } from '../screens/third-party-redirect/ThirdPartyRedirectScreen';

export type TPRedirectMachineViewProps = { provider: ProviderEntity } & UseTPRedirectMachineProps &
  BackButton;

export const TPRedirectMachineView = ({
  provider,
  credentials,
  onBack,
}: TPRedirectMachineViewProps) => {
  const [current, send] = useTPRedirectMachine({ credentials });
  const isClientVerified = useAppSelector((state) => state.oauth.description!.verified);
  const clientName = useAppSelector((state) => state.oauth.description!.clientName);
  const { isMobile } = userAgent;
  const { market } = useUrlQuery();
  const isMarketUS: boolean = market === MarketCode.US;

  const { sendEvent, analytics } = useApplicationEvent({
    applicationEvent: ApplicationEvent.THIRD_PARTY_REDIRECT_MACHINE,
  });

  useEffect(() => {
    const autoRedirectMobileEvent = getAutoRedirectMobileQueryParamAnalytic();

    if (autoRedirectMobileEvent) analytics.sendInteractionEventOnce(autoRedirectMobileEvent);
  }, [analytics]);

  const openRedirect = useCallback(() => {
    send({ type: 'USER_CLICK_REDIRECT_EVENT' });
  }, [send]);

  if (current.matches('APPLICATION_ERROR')) {
    sendEvent(ApplicationEvent.THIRD_PARTY_REDIRECT_MACHINE_ERROR);
    throw current.context.error;
  }

  if (current.matches('OPEN_REDIRECT_VIEW')) {
    if (isMobile) {
      return (
        <OpenThirdPartyRedirectScreen
          redirectUrl={current.context.redirector!.url}
          provider={provider}
          isVerified={isClientVerified}
          onButtonClick={openRedirect}
          onBack={onBack}
        />
      );
    }

    if (isMarketUS) {
      return (
        <ThirdPartyRedirectScreen
          redirectUrl={current.context.redirector!.url}
          provider={provider}
          isVerified={isClientVerified}
          onButtonClick={openRedirect}
          onBack={onBack}
        />
      );
    }

    return (
      <ThirdPartyRedirectInstructionsScreen
        provider={provider}
        clientName={clientName}
        isVerified={isClientVerified}
        onClick={openRedirect}
        onBack={onBack}
      />
    );
  }

  if (current.matches('REDIRECT_PENDING_VIEW')) {
    return (
      <ThirdPartyRedirectPendingScreen
        credentials={current.context.credentials}
        providerName={provider.displayName}
        onBack={onBack}
      />
    );
  }

  if (current.matches('FLOW_FINISHED')) {
    return <FlowFinished />;
  }

  return <LoadingOverlay />;
};
